import { ShaderProgram } from './ShaderProgram';
import { FrameBuffer } from './FrameBuffer';
import type { FullscreenQuad } from './FullscreenQuad';

import extractFrag from '../shaders/bloom-extract.frag.glsl?raw';
import blurFrag from '../shaders/bloom-blur.frag.glsl?raw';
import fullscreenVert from '../shaders/fullscreen.vert.glsl?raw';

const MIP_LEVELS = 3;
const BLOOM_THRESHOLD = 0.8;

/**
 * Multi-pass bloom effect with progressive down/upsampling.
 * Extract bright pixels → downsample with blur → upsample and blend.
 */
export class BloomPass {
  private _gl: WebGL2RenderingContext;
  private _quad: FullscreenQuad;
  private _extractShader: ShaderProgram;
  private _blurShader: ShaderProgram;
  private _mips: FrameBuffer[] = [];
  private _pingPong: FrameBuffer[] = [];
  private _mipLevels: number;

  constructor(
    gl: WebGL2RenderingContext,
    quad: FullscreenQuad,
    width: number,
    height: number,
  ) {
    this._gl = gl;
    this._quad = quad;
    this._mipLevels = MIP_LEVELS;
    this._extractShader = new ShaderProgram(gl, fullscreenVert, extractFrag);
    this._blurShader = new ShaderProgram(gl, fullscreenVert, blurFrag);
    this._createMips(width, height);
  }

  get resultTexture(): WebGLTexture {
    return this._mips[0].texture;
  }

  render(hdrTexture: WebGLTexture): void {
    const gl = this._gl;

    this._extractBright(hdrTexture);

    for (let i = 1; i < this._mipLevels; i++) {
      this._downsampleBlur(this._mips[i - 1].texture, this._mips[i], this._pingPong[i]);
    }

    for (let i = this._mipLevels - 1; i > 0; i--) {
      this._upsampleBlend(this._mips[i].texture, this._mips[i - 1]);
    }

    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
  }

  resize(width: number, height: number): void {
    this._disposeMips();
    this._createMips(width, height);
  }

  dispose(): void {
    this._extractShader.dispose();
    this._blurShader.dispose();
    this._disposeMips();
  }

  private _extractBright(source: WebGLTexture): void {
    const gl = this._gl;
    this._mips[0].bind();
    gl.clear(gl.COLOR_BUFFER_BIT);

    this._extractShader.use();
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, source);
    this._extractShader.setInt('u_texture', 0);
    this._extractShader.setFloat('u_threshold', BLOOM_THRESHOLD);
    this._quad.draw();
  }

  private _downsampleBlur(
    source: WebGLTexture, target: FrameBuffer, ping: FrameBuffer,
  ): void {
    const gl = this._gl;
    const w = target.width;
    const h = target.height;

    ping.bind();
    gl.clear(gl.COLOR_BUFFER_BIT);
    this._blurShader.use();
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, source);
    this._blurShader.setInt('u_texture', 0);
    this._blurShader.setVec2('u_direction', 1.0 / w, 0.0);
    this._quad.draw();

    target.bind();
    gl.clear(gl.COLOR_BUFFER_BIT);
    gl.bindTexture(gl.TEXTURE_2D, ping.texture);
    this._blurShader.setVec2('u_direction', 0.0, 1.0 / h);
    this._quad.draw();
  }

  private _upsampleBlend(source: WebGLTexture, target: FrameBuffer): void {
    const gl = this._gl;
    target.bind();

    gl.enable(gl.BLEND);
    gl.blendFunc(gl.ONE, gl.ONE);

    this._blurShader.use();
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, source);
    this._blurShader.setInt('u_texture', 0);
    this._blurShader.setVec2('u_direction', 0.0, 0.0);
    this._quad.draw();

    gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
  }

  private _createMips(width: number, height: number): void {
    const gl = this._gl;
    let w = Math.floor(width / 2);
    let h = Math.floor(height / 2);

    for (let i = 0; i < this._mipLevels; i++) {
      this._mips.push(new FrameBuffer(gl, w, h, gl.RGBA16F, gl.LINEAR, false));
      this._pingPong.push(new FrameBuffer(gl, w, h, gl.RGBA16F, gl.LINEAR, false));
      w = Math.max(1, Math.floor(w / 2));
      h = Math.max(1, Math.floor(h / 2));
    }
  }

  private _disposeMips(): void {
    for (let i = 0; i < this._mips.length; i++) {
      this._mips[i].dispose();
      this._pingPong[i].dispose();
    }
    this._mips.length = 0;
    this._pingPong.length = 0;
  }
}
